import React, { ReactElement, Suspense, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/Auth';
import { checkMFAStatus, getListMFAEnabled, getTenantDetails } from '../../auth/_requests';
import { AuthBrandedLoader } from '../../pages/Login/AuthBrandedLoader';
import { useProfileImage } from '../hooks/profilePhoto';
import { buildingDetails, tenantDetails } from '../utils/types';
import { useDomainStatus } from '../hooks/DomainStatus';

interface PrivateRouteProps {
  children:
    | ReactElement<{ tenant: tenantDetails; buildings?: buildingDetails[] }>
    | ReactElement<{ tenant?: tenantDetails; buildings?: buildingDetails[] }>;
  requiredVerifications: string[];
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, requiredVerifications }) => {
  const { auth, logout } = useAuth();
  const [, setIsValid] = useState(false);
  const [loading, setLoading] = useState(true);
  const { setProfileImageUrl, photoUpdated } = useProfileImage();
  const [tenant, setTenant] = useState<tenantDetails>();
  const { domainStatus } = useDomainStatus();
  const navigate = useNavigate();
  useEffect(() => {
    const checkValidity = async () => {
      const token = auth?.data.token;
      if (token) {
        try {
          const response = await getTenantDetails(token);
          const tenant = response.data;
          const profileUrl = tenant.user.profilePictureUrl;
          setProfileImageUrl(profileUrl);
          setTenant(tenant);
          if (localStorage.getItem('isSignUp') !== 'true') {
            try {
              const response = await checkMFAStatus(auth.data.token);
              const mfaReseponse = await getListMFAEnabled(auth.data.token);
              if (response?.data.isMfaRequired && tenant.user.isEmailVerified) {
                const emailMFAMap = mfaReseponse?.data.find((mfa) => mfa.name === 'email');
                const phoneMFAMap = mfaReseponse?.data.find((mfa) => mfa.name === 'phoneNumber');
                const emailMFA = String(emailMFAMap?.isEnabled) === 'true' ? true : false;
                const phoneMFA = String(phoneMFAMap?.isEnabled) === 'true' ? true : false;

                if (emailMFA && phoneMFA) {
                  navigate('/select-two-factor-authentication');
                } else if (emailMFA) {
                  navigate('/two-factor-authentication/email');
                } else navigate('/two-factor-authentication/phone');
                return;
              }
            } catch (error) {
              console.error('Error checking MFA status:', error);
            }
          }
          if (tenant.tenant.tenantIdv && tenant.tenant.tenantIdv.status === 'declined') {
            logout();
            return;
          }
          const conditionsMet = requiredVerifications.every((verification: string) => {
            if (verification === 'isTenant') return tenant.user.accessToken !== '';
            if (verification === 'isEmailVerified') return tenant.user.isEmailVerified === 1;
            if (verification === 'isPhoneVerified') return tenant.user.isPhoneVerified === 1;
            if (verification === 'isNonIndividualAccount')
              return tenant.tenant.accountType === 'personal';
            if (verification === 'isIdentityVerified')
              return tenant.tenant.isIdentityVerified === 1;
            if (verification === 'isDomainValid') return domainStatus === 'valid';
            return true; // Default case
          });
          if (conditionsMet) {
            setIsValid(true);
          } else {
            logout(); // Logout if any condition fails
          }
        } catch (error) {
          console.error('Error fetching tenant details:', error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    checkValidity();
  }, [auth, logout, requiredVerifications, photoUpdated]);

  useEffect(() => {
    if (!auth) {
      navigate('/', { replace: true });
    }
  }, [auth, navigate]);

  if (loading) {
    return <AuthBrandedLoader message='Loading…' />;
  }

  return (
    <Suspense fallback={<AuthBrandedLoader message='Loading…' />}>
      {React.cloneElement(children, { tenant })}
    </Suspense>
  );
};

export default PrivateRoute;
